import type { AcuityLearnGrants, Membership } from "@/contexts/AuthContext";

export type BadgeTone =
  | "super"
  | "admin"
  | "teacher"
  | "student"
  | "curriculum"
  | "counselor"
  | "parent"
  | "global"
  | "listed"
  | "school"
  | "muted";

export interface SessionBadge {
  key: string;
  label: string;
  sub?: string;
  tone: BadgeTone;
  icon: string;
}

export function displayName(user: {
  preferredName?: string | null;
  firstName?: string;
  lastName?: string;
  email?: string;
} | null): string {
  if (!user) return "Account";
  const preferred = (user.preferredName || "").trim();
  if (preferred) return preferred;
  const full = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return full || user.email || "Account";
}

export function membershipLearnView(
  m: Membership | null,
  fallback?: {
    roles?: AcuityLearnGrants["roles"];
    schoolScope?: AcuityLearnGrants["schoolScope"] | null;
    resolvedSchool?: AcuityLearnGrants["resolvedSchool"] | null;
  } | null,
) {
  const grants = m?.acuityLearn || fallback || null;
  return {
    badges: roleBadges(grants?.roles),
    scope: scopeBadge(grants?.schoolScope, grants?.resolvedSchool),
    uis: membershipParts(m),
  };
}

export function membershipParts(m: Membership | null) {
  if (!m) {
    return { group: "—", contextLine: "—", period: "—", line: "—" };
  }
  const group = m.group_name || m.groups_periods_title || "—";
  const parent = (m.parent_group_name || "").trim();
  const org = (m.organization_group_name || m.organization_groups_periods_title || "").trim();
  const bits = [group];
  if (parent) bits.push(parent);
  if (org && org.toLowerCase() !== parent.toLowerCase()) bits.push(org);
  const period = (m.period_title || m.period_code || "").trim();
  if (period) bits.push(period);
  const contextBits = [];
  if (parent) contextBits.push(parent);
  if (org && org.toLowerCase() !== parent.toLowerCase()) contextBits.push(org);
  return {
    group,
    contextLine: contextBits.length ? contextBits.join(" · ") : "—",
    period: period || "—",
    line: bits.filter(Boolean).join(" · "),
  };
}

export function roleBadges(roles: AcuityLearnGrants["roles"] = {}): SessionBadge[] {
  const badges: SessionBadge[] = [];
  if (roles.superAdmin) {
    badges.push({ key: "superAdmin", label: "Super Admin", tone: "super", icon: "shield" });
  }
  if (roles.admin) badges.push({ key: "admin", label: "Admin", tone: "admin", icon: "key" });
  if (roles.teacher) badges.push({ key: "teacher", label: "Teacher", tone: "teacher", icon: "guide" });
  if (roles.student) badges.push({ key: "student", label: "Student", tone: "student", icon: "learner" });
  if (roles.curriculumDeveloper) {
    badges.push({
      key: "curriculumDeveloper",
      label: "Curriculum Developer",
      tone: "curriculum",
      icon: "curriculum",
    });
  }
  if (roles.counselor) {
    badges.push({ key: "counselor", label: "Counselor", tone: "counselor", icon: "counselor" });
  }
  if (roles.parent) {
    badges.push({ key: "parent", label: "Parent", tone: "parent", icon: "parent" });
  }
  return badges;
}

export function scopeBadge(
  schoolScope?: AcuityLearnGrants["schoolScope"] | null,
  resolvedSchool?: AcuityLearnGrants["resolvedSchool"] | null,
): SessionBadge | null {
  if (schoolScope?.type === "global") {
    return { key: "global", label: "Global", sub: "All schools", tone: "global", icon: "globe" };
  }
  if (schoolScope?.type === "listed") {
    const n = schoolScope.groupIds?.length || 0;
    return {
      key: "listed",
      label: "Selected",
      sub: n ? `${n} schools` : "Selected schools",
      tone: "listed",
      icon: "list",
    };
  }
  const schoolName = resolvedSchool?.groupName || "Current school";
  if (schoolScope?.type === "membership" || resolvedSchool) {
    return {
      key: "membership",
      label: schoolName,
      sub: "Current school",
      tone: "school",
      icon: "school",
    };
  }
  return null;
}

/**
 * School-scoped users who cannot create schools have nothing to do on the list.
 * Membership is always one school; listed counts as single-scope only when it is one id.
 */
export function autoOpenSchoolId(grants?: AcuityLearnGrants | null): string | null {
  if (!grants) return null;
  if (grants.capabilities?.createSchools || grants.capabilities?.scopeGlobal) return null;
  const scope = grants.schoolScope;
  if (!scope || scope.type === "global" || scope.type === "none") return null;
  const ids = (scope.groupIds || []).map(String).filter(Boolean);
  if (scope.type === "membership") {
    return ids[0] || (grants.resolvedSchool?.groupId ? String(grants.resolvedSchool.groupId) : null);
  }
  if (scope.type === "listed" && ids.length === 1) return ids[0];
  return null;
}
